import { targets, useReferentialContext } from "../../Context";
import React, { useEffect, useState } from "react";
import {
    CompetitiveGames,
    CompetitiveRoundSummaries,
    CompetitiveRoundSummariesDeathEventInner,
    Maps,
    Player,
    Team
} from "../../reckon-api";
import WindowContainer from "../../components/layout/WindowContainer";
import { MultipleGameSelectorDialog } from "../../components/selectors/Competitive/SpecificGameSelectorDialog";
import { capitalize, Chip, Grid, Stack, Typography, useTheme } from "@mui/material";
import { NeonToggleButton } from "../../NeonComponents/NeonToggle";
import { NeonCard, NeonCardContent, NeonCardHeader } from "../../NeonComponents/NeonCards";
import { scrollable, getNeonColor, attacker_color, defender_color } from "../../theme";
import { HeatmapKillsRender, KillEventWithLocation } from "../../components/Maps/HeatmapKillsRender";
import Box from "@mui/material/Box";
import { NeonTab, NeonTabs } from "../../NeonComponents/NeonTabs";
import { GpsFixed, Visibility, VisibilityOff } from "@mui/icons-material";

type KillDisplayMode = 'kills' | 'deaths' | 'both';
type SideFilter = 'all' | 'Attacker' | 'Defender';

export default function KillsHeatmap(props: { target: targets }) {
    const context = useReferentialContext();
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    // Selection state
    const [team, setTeam] = useState(context.selectedTeam as Team | null);
    const [games, setGames] = useState([] as CompetitiveGames[]);
    const [players, setPlayers] = useState([] as Player[]);
    const [sideFilter, setSideFilter] = useState<SideFilter>('all');
    const [displayMode, setDisplayMode] = useState<KillDisplayMode>('kills');
    const [selectedMap, setSelectedMap] = useState<Maps | undefined>(undefined);
    
    // Data state
    const [roundSummaries, setRoundSummaries] = useState([] as CompetitiveRoundSummaries[]);
    const [killEvents, setKillEvents] = useState([] as KillEventWithLocation[]);
    const [loading, setLoading] = useState(false);

    // Get unique maps from selected games
    const uniqueMaps = [...new Set(games.map(g => g.map))];
    
    // When games change, auto-select map if only one
    useEffect(() => {
        if (uniqueMaps.length === 1) {
            const map = context.mapsReferential.find(m => m.id === uniqueMaps[0]);
            setSelectedMap(map);
        } else if (uniqueMaps.length > 1 && !selectedMap) {
            const map = context.mapsReferential.find(m => m.id === uniqueMaps[0]);
            setSelectedMap(map);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [games, context.mapsReferential]);

    // Fetch round summaries when games/players/side change
    useEffect(() => {
        if (games.length === 0 || !team) {
            setRoundSummaries([]);
            setKillEvents([]);
            return;
        }

        setLoading(true);

        // Filter games by selected map
        const filteredGames = selectedMap 
            ? games.filter(g => g.map === selectedMap.id)
            : games;

        if (filteredGames.length === 0) {
            setRoundSummaries([]);
            setKillEvents([]);
            setLoading(false);
            return;
        }

        const requestParams: any = {
            additional_filters: {
                game__in: filteredGames.map(g => g.id).join(','),
                team1: team.id,
            },
            // Only fetch the fields we need for the heatmap
            only_values: ['kill_events,death_event,kills'],
        };

        // Add player filter if players selected
        if (players.length > 0) {
            requestParams.additional_filters.player__in = players.map(p => p.id).join(',');
        }

        // Add side filter
        if (sideFilter !== 'all') {
            requestParams.additional_filters.side = sideFilter;
        }

        context.reckonApi.competitiveRoundSummaries.list(requestParams)
            .then(summaries => {
                console.log('Received summaries:', summaries.length);
                if (summaries.length > 0) {
                    console.log('First summary keys:', Object.keys(summaries[0]));
                    console.log('First summary kill_events:', summaries[0].kill_events);
                    console.log('First summary death_event:', summaries[0].death_event);
                }
                setRoundSummaries(summaries);
                
                // Extract kill events based on display mode
                // Note: death_event and kill_events are now arrays of CompetitiveRoundSummariesDeathEventInner
                const events: KillEventWithLocation[] = [];
                
                summaries.forEach(summary => {
                    if (displayMode === 'deaths' || displayMode === 'both') {
                        // Add death events (where this player died)
                        // death_event is now an array
                        if (summary.death_event && Array.isArray(summary.death_event)) {
                            summary.death_event.forEach((deathEvent: CompetitiveRoundSummariesDeathEventInner) => {
                                events.push(deathEvent as KillEventWithLocation);
                            });
                        }
                    }
                    
                    if (displayMode === 'kills' || displayMode === 'both') {
                        // kill_events is now an array (not a JSON string anymore)
                        if (summary.kill_events && Array.isArray(summary.kill_events)) {
                            summary.kill_events.forEach((killEvent: CompetitiveRoundSummariesDeathEventInner) => {
                                events.push(killEvent as KillEventWithLocation);
                            });
                        }
                    }
                });

                console.log('Total events collected:', events.length);
                if (events.length > 0) {
                    console.log('First event structure:', JSON.stringify(events[0], null, 2));
                }
                setKillEvents(events);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error fetching round summaries:', err);
                setLoading(false);
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [games, team, players, sideFilter, displayMode, selectedMap]);

    // Get team players
    const teamPlayers = context.playersReferential.filter(p => p.current_team === team?.id);

    // Toggle player selection
    const togglePlayer = (player: Player) => {
        if (players.find(p => p.id === player.id)) {
            setPlayers(players.filter(p => p.id !== player.id));
        } else {
            setPlayers([...players, player]);
        }
    };

    // Stats summary
    // kills is now a number, not an array
    const totalKills = roundSummaries.reduce((acc, s) => acc + (s.kills || 0), 0);
    // death_event is now an array
    const totalDeaths = roundSummaries.reduce((acc, s) => acc + (s.death_event?.length || 0), 0);
    const headshotKills = killEvents.filter(k => k.is_headshot).length;
    const firstBloods = killEvents.filter(k => k.is_first_blood).length;

    return (
        <WindowContainer direction="column" spacing={2}>
            {/* Header with game selector */}
            <MultipleGameSelectorDialog
                games={games}
                setGames={setGames}
                target={props.target}
                team={team}
                setTeam={setTeam}
                sameMap={true}
            />

            <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                {/* Left Panel - Filters */}
                <Grid size={3}>
                    <NeonCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <NeonCardHeader title="Filters" sx={{ pb: 1 }} />
                        <NeonCardContent sx={{ flex: 1, ...scrollable }}>
                            <Stack spacing={2}>
                                {/* Map Selection (if multiple maps) */}
                                {uniqueMaps.length > 1 && (
                                    <Box>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}>
                                            Map
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                            {uniqueMaps.map(mapId => {
                                                const map = context.mapsReferential.find(m => m.id === mapId);
                                                return (
                                                    <Chip
                                                        key={mapId}
                                                        label={capitalize(mapId)}
                                                        size="small"
                                                        onClick={() => setSelectedMap(map)}
                                                        sx={{
                                                            backgroundColor: selectedMap?.id === mapId 
                                                                ? `${neonColor}30` 
                                                                : 'rgba(255,255,255,0.08)',
                                                            border: selectedMap?.id === mapId 
                                                                ? `1px solid ${neonColor}` 
                                                                : '1px solid transparent',
                                                            '&:hover': {
                                                                backgroundColor: `${neonColor}20`,
                                                            }
                                                        }}
                                                    />
                                                );
                                            })}
                                        </Stack>
                                    </Box>
                                )}

                                {/* Display Mode */}
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}>
                                        Display Mode
                                    </Typography>
                                    <NeonTabs 
                                        value={displayMode} 
                                        onChange={(_, v) => setDisplayMode(v)}
                                        sx={{ minHeight: '36px' }}
                                    >
                                        <NeonTab 
                                            value="kills" 
                                            label="Kills" 
                                            sx={{ minHeight: '36px', py: 0 }}
                                        />
                                        <NeonTab 
                                            value="deaths" 
                                            label="Deaths" 
                                            sx={{ minHeight: '36px', py: 0 }}
                                        />
                                        <NeonTab 
                                            value="both" 
                                            label="Both" 
                                            sx={{ minHeight: '36px', py: 0 }}
                                        />
                                    </NeonTabs>
                                </Box>

                                {/* Side Filter */}
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}>
                                        Side
                                    </Typography>
                                    <Stack direction="row" spacing={0.5}>
                                        <NeonToggleButton
                                            value="all"
                                            selected={sideFilter === 'all'}
                                            onClick={() => setSideFilter('all')}
                                            sx={{ flex: 1, fontSize: '0.75rem' }}
                                        >
                                            All
                                        </NeonToggleButton>
                                        <NeonToggleButton
                                            value="Attacker"
                                            selected={sideFilter === 'Attacker'}
                                            onClick={() => setSideFilter('Attacker')}
                                            sx={{ 
                                                flex: 1, 
                                                fontSize: '0.75rem',
                                                '&.Mui-selected': {
                                                    backgroundColor: `${attacker_color}50`,
                                                    borderColor: attacker_color,
                                                }
                                            }}
                                        >
                                            ATK
                                        </NeonToggleButton>
                                        <NeonToggleButton
                                            value="Defender"
                                            selected={sideFilter === 'Defender'}
                                            onClick={() => setSideFilter('Defender')}
                                            sx={{ 
                                                flex: 1, 
                                                fontSize: '0.75rem',
                                                '&.Mui-selected': {
                                                    backgroundColor: `${defender_color}50`,
                                                    borderColor: defender_color,
                                                }
                                            }}
                                        >
                                            DEF
                                        </NeonToggleButton>
                                    </Stack>
                                </Box>

                                {/* Player Selection */}
                                <Box>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1, display: 'block' }}>
                                        Players {players.length > 0 && `(${players.length} selected)`}
                                    </Typography>
                                    <Stack spacing={0.5}>
                                        {teamPlayers.map(player => (
                                            <NeonToggleButton
                                                key={player.id}
                                                value={player.id}
                                                selected={players.some(p => p.id === player.id)}
                                                onClick={() => togglePlayer(player)}
                                                sx={{
                                                    justifyContent: 'flex-start',
                                                    px: 1.5,
                                                    py: 0.75,
                                                }}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography sx={{ fontSize: '0.8rem' }}>
                                                        {player.id}
                                                    </Typography>
                                                </Stack>
                                            </NeonToggleButton>
                                        ))}
                                        {teamPlayers.length === 0 && (
                                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
                                                Select a team to see players
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>

                                {/* Stats Summary */}
                                {games.length > 0 && (
                                    <Box sx={{ 
                                        mt: 2, 
                                        p: 1.5, 
                                        borderRadius: '8px',
                                        background: 'rgba(255,255,255,0.03)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1.5, display: 'block' }}>
                                            Summary
                                        </Typography>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    Games
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {games.filter(g => !selectedMap || g.map === selectedMap.id).length}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    Rounds
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {roundSummaries.length}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: '#2ecc71' }}>
                                                    Kills
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#2ecc71' }}>
                                                    {totalKills}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: '#e74c3c' }}>
                                                    Deaths
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#e74c3c' }}>
                                                    {totalDeaths}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    Headshots
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {headshotKills}
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between">
                                                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>
                                                    First Bloods
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                                                    {firstBloods}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </NeonCardContent>
                    </NeonCard>
                </Grid>

                {/* Right Panel - Map Heatmap */}
                <Grid size={9}>
                    <NeonCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <NeonCardHeader 
                            title={
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <GpsFixed sx={{ fontSize: '1.2rem', color: neonColor }} />
                                    <Typography variant="h6">
                                        {selectedMap ? capitalize(selectedMap.id) : 'Select a game'}
                                    </Typography>
                                    {killEvents.length > 0 && (
                                        <Chip 
                                            size="small" 
                                            label={`${killEvents.length} events`}
                                            sx={{ 
                                                backgroundColor: `${neonColor}20`,
                                                borderColor: neonColor,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    )}
                                </Stack>
                            }
                            sx={{ pb: 1 }}
                        />
                        <NeonCardContent sx={{ flex: 1, minHeight: 0, p: 2 }}>
                            {selectedMap ? (
                                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                                    {loading ? (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                        }}>
                                            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                                Loading...
                                            </Typography>
                                        </Box>
                                    ) : killEvents.length > 0 ? (
                                        <HeatmapKillsRender
                                            map={selectedMap}
                                            id={selectedMap.id}
                                            kills={killEvents}
                                        />
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            height: '100%',
                                            flexDirection: 'column',
                                            gap: 2,
                                        }}>
                                            <VisibilityOff sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.2)' }} />
                                            <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                                No kill events found for the selected filters.
                                                <br />
                                                Try selecting different games or adjusting filters.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '100%',
                                    flexDirection: 'column',
                                    gap: 2,
                                }}>
                                    <Visibility sx={{ fontSize: '3rem', color: 'rgba(255,255,255,0.2)' }} />
                                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                        Select games to view kill heatmap.
                                        <br />
                                        Use the "Change Game Set" button above.
                                    </Typography>
                                </Box>
                            )}
                        </NeonCardContent>
                    </NeonCard>
                </Grid>
            </Grid>
        </WindowContainer>
    );
}
